import React, { FunctionComponent } from 'react';
import { ParsedPromo, PromoTypes } from '../types';
import dompurify from 'dompurify';
import { Heading } from '../../../shared/Heading';
import { Paragraph } from '../../../shared/Paragraph';
import defaultPromoImg from '../../../../assets/Promotions/promo-slot.png';
import { CardCTASection } from './CardCTASection/CardCTASection';
import { useNavigate } from 'react-router-dom';
import { ProgressBar } from '../../../shared/ProgressBar';
import { mockWage, mockWageRequired } from '../consts';
import { isProgressBar, showCurrency } from '../utils';
import './styles';
import { useDispatch } from 'react-redux';
import { setActivePromoType } from '../../../../state/promotions/reducers';

interface PromotionCardProps {
  isActivePromotions?: boolean;
  promo: ParsedPromo;
  showButtons?: boolean;
  isWide?: boolean;
  setShowPromoPrompt?: (arg: boolean) => void;
  setClaimedPromo?: (arg: ParsedPromo) => void;
}

export const PromotionCard: FunctionComponent<PromotionCardProps> = ({
  promo,
  isWide,
  showButtons = true,
  isActivePromotions,
  setShowPromoPrompt,
  setClaimedPromo,
}): JSX.Element => {
  const sanitizer = dompurify.sanitize;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const promoType = promo.promoType?.toLowerCase();
  const showPrimary = promoType !== PromoTypes.NETWORK;

  const wageNumber = Number(promo.wageredAmount ?? mockWage);
  const wageReqNumber = Number(promo.wageringRequirementAmount ?? mockWageRequired);

  const { title, subtitle, description, imgPath, wideImgPath, pageUrl = '', pageSubUrl = '' } = promo;

  const showProgressBar = isProgressBar(promo.status, promo.promoType);
  const resImgPath = (isWide && wideImgPath ? wideImgPath : imgPath) || defaultPromoImg;

  const goToPromoPage = (): void => navigate(`${pageUrl}/${pageSubUrl}`);

  const onClaimClick = (): void => {
    setShowPromoPrompt?.(true);
    setClaimedPromo?.(promo);
    dispatch(setActivePromoType(promo?.promoType as PromoTypes));
  };

  return (
    <>
      <div
        className="promotion-card"
        style={{
          backgroundImage: `url(${resImgPath})`,
        }}
        onClick={goToPromoPage}
      >
        <div className="promotion-card__container">
          <div className="promotion-card__content">
            <div className="promotion-card__titles">
              <Heading className="promotion-card__title" noMargin level={4}>
                {title}
              </Heading>
              {subtitle && (
                <Heading className="promotion-card__subtitle" noMargin level={4}>
                  {subtitle}
                </Heading>
              )}
            </div>

            {description && (
              <Paragraph className="promotion-card__description" noMargin>
                <span dangerouslySetInnerHTML={{ __html: sanitizer(description) }} />
              </Paragraph>
            )}
          </div>

          {showProgressBar && (
            <ProgressBar
              showWageringInfo
              value={wageNumber}
              type="success"
              min={0}
              max={wageReqNumber}
              showCurrency={showCurrency(promo.type)}
            />
          )}

          {showButtons && (
            <CardCTASection
              onClaimClick={onClaimClick}
              onViewDetailsClick={goToPromoPage}
              promo={promo}
              isActivePromotions={isActivePromotions}
              showPrimary={showPrimary}
            />
          )}
        </div>
      </div>
    </>
  );
};
